import { useState } from 'react'
import { usePersistedState } from '@/lib/usePersistedState'
import { useIsPhone, useMediaQuery } from '@/lib/useMediaQuery'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { api, Capabilities } from '@/api/client'
import { formatValue } from '@/charts/palette'
import { useMe } from '@/features/auth/useAuth'
import { EditableGrid } from './EditableGrid'
import { Select } from '@/components/Select'
import { SearchSelect } from './SearchSelect'
import { FilterButton, FilterSheet } from '@/components/FilterSheet'
import { projectName } from '@/i18n/data'
import { LogoLoaderPill } from '@/components/LogoLoader'
import { useFileDownload } from '@/lib/useFileDownload'
import { useLoadingGate } from '@/lib/useLoadingGate'
import type { AufmassPage as PageData, FilterOptions } from './types'
import type { ReactNode } from 'react'
import { fmtDate, fmtNumber, translator, useT } from '@/i18n'
import { aufmassMessages } from '@/i18n/messages/aufmass'
import { commonMessages } from '@/i18n/messages/common'

const PAGE_SIZES = [100, 250, 500, 1000]

/** Mirrors AufmassEndpoints.MaxExcelRows — the API refuses an .xlsx export above it. */
const MAX_EXCEL_ROWS = 20_000

// The grid fills the viewport left of the dark filter rail. It starts just under the nav pill —
// AppShell publishes the pill's measured height as --nav-h — and 312px leaves room for the rail
// on the right (w-72 panel + margins).
const GRID_TOP = 'calc(var(--nav-h, 4rem) + 0.5rem)'
const RIGHT_INSET = 312

// Below lg the same controls sit in a full-screen sheet, so they grow to a 44px touch target.
const DARK_INPUT =
  'w-full rounded-lg border border-neutral-700 bg-neutral-800 px-2.5 py-1.5 text-xs text-neutral-100 placeholder-neutral-500 focus:border-brand-500 focus:outline-none max-lg:min-h-11 max-lg:rounded-xl max-lg:py-2.5'

interface Freshness {
  refreshedAt: string | null
  lastChanged: string | null
  dataThrough: string | null
  rowUpdatedAt: string | null
}

/** "just now" / "12 min ago" / "3 h ago" / "2 d ago" from an ISO timestamp, in the current language. */
function timeAgo(iso: string | null): string {
  if (!iso) return '—'
  const t = translator(aufmassMessages)
  const s = (Date.now() - new Date(iso).getTime()) / 1000
  if (s < 90) return t('justNow')
  if (s < 5400) return t('minAgo', { count: Math.round(s / 60) })
  if (s < 172800) return t('hoursAgo', { count: Math.round(s / 3600) })
  return t('daysAgo', { count: Math.round(s / 86400) })
}

/**
 * The Aufmaß screen: the workbook, full-bleed and editable.
 *
 * The grid owns the whole viewport and the chrome floats over it, because this replaced a
 * boxed read-only table and the thing people actually do here is read across ~55 columns.
 * Every pixel spent on page furniture is a column they have to scroll for.
 *
 * The floating cluster collapses. It is genuinely in the way when you are working the
 * right-hand end of the row, and a panel that cannot be dismissed is just a smaller screen.
 */
export function AufmassPage() {
  const { data: me } = useMe()
  const t = useT(aufmassMessages)
  const tc = useT(commonMessages)
  const canEdit = me?.capabilities.includes(Capabilities.EditAufmass) ?? false

  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = usePersistedState('aufmass:pageSize', 500)
  const [sort, setSort] = useState('work_date')
  const [dir, setDir] = useState<'asc' | 'desc'>('desc')
  const [search, setSearch] = useState('')
  const [projectId, setProjectId] = useState('')
  const [companyId, setCompanyId] = useState('')
  // Off by default: the server already hides "ghost" crew-days (no work, no crew, no company).
  // Crewed clock-ins with no work stay visible so you can see who clocked in. This toggle is the
  // stricter opt-in — only crew-days that actually recorded work.
  const [onlyWithWork, setOnlyWithWork] = useState(false)
  const [presence, setPresence] = useState('')
  const [cable, setCable] = useState('')
  // The filter rail hides so the grid can own the whole width, like the RTK panels.
  const [railOpen, setRailOpen] = usePersistedState('aufmass:railOpen', true)
  const isPhone = useIsPhone()
  const isShort = useMediaQuery('(max-height: 500px) and (orientation: landscape)')
  const [mobileRail, setMobileRail] = useState(false)
  const showRail = isPhone ? mobileRail : railOpen
  const openRail = () => { isPhone ? setMobileRail(true) : setRailOpen(true) }
  const closeRail = () => { isPhone ? setMobileRail(false) : setRailOpen(false) }
  // How the column headers read, and whether blank position columns are dropped — user's choice.
  const [headerMode, setHeaderMode] = usePersistedState<'full' | 'short'>('aufmass:headerMode', 'full')
  const [hideEmpty, setHideEmpty] = usePersistedState('aufmass:hideEmpty', false)
  const [zoom, setZoom] = usePersistedState('aufmass:zoom', 1)
  const stepZoom = (d: number) => setZoom((z) => Math.min(2, Math.max(0.5, Math.round((z + d) * 100) / 100)))
  const [showPositions, setShowPositions] = usePersistedState('aufmass:showPositions', true)

  const excel = useFileDownload()
  const excelLoad = useLoadingGate(excel.state.status === 'working')

  const { data: filters } = useQuery<FilterOptions>({
    queryKey: ['aufmass-filters'],
    queryFn: () => api.get<FilterOptions>('/api/aufmass/filters'),
    staleTime: 5 * 60_000,
  })

  const { data: freshness } = useQuery<Freshness>({
    queryKey: ['aufmass-freshness'],
    queryFn: () => api.get<Freshness>('/api/aufmass/freshness'),
    refetchInterval: 60_000,
  })

  const params = new URLSearchParams({
    page: String(page), pageSize: String(pageSize), sort, dir,
  })
  if (search.trim()) params.set('search', search.trim())
  if (projectId) params.set('projectId', projectId)
  if (companyId) params.set('companyId', companyId)
  if (onlyWithWork) params.set('onlyWithWork', 'true')
  if (presence) params.set('presence', presence)
  if (cable) params.set('cable', cable)

  // The grid patches this exact key optimistically, so it must be the one used to fetch.
  const queryKey = ['aufmass', params.toString()] as const

  const { data, error } = useQuery<PageData>({
    queryKey,
    queryFn: () => api.get<PageData>(`/api/aufmass/?${params}`),
    placeholderData: keepPreviousData,
  })

  const reset = <T,>(setter: (v: T) => void) => (v: T) => { setter(v); setPage(1) }

  // Column-header sort: clicking the active column flips direction, a new column starts ascending.
  const onSort = (key: string) => {
    if (key === sort) setDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    else { setSort(key); setDir('asc') }
    setPage(1)
  }
  const totalPages = data ? Math.max(1, Math.ceil(data.total / pageSize)) : 1
  const edited = (data?.rows ?? []).reduce(
    (n, r) => n + Object.keys(r.overrides ?? {}).length, 0)

  // What the Filters button counts and Reset clears: the narrowing filters, not view settings
  // (sort, headers, zoom) — those are preferences, and resetting them would feel like data loss.
  const activeFilters = [search.trim(), projectId, companyId, presence, cable, onlyWithWork]
    .filter(Boolean).length
  const resetFilters = () => {
    setSearch(''); setProjectId(''); setCompanyId(''); setPresence(''); setCable('')
    setOnlyWithWork(false); setPage(1)
  }

  // The rail (desktop) and the sheet (phone) show the same controls, laid out for each.
  const stats = (
    <div className="grid grid-cols-2 gap-x-3 gap-y-2.5">
      <DStat label={t('crewDaysLabel')} value={fmtNumber(data?.total ?? 0)} />
      <DStat label={tc('metres')} value={formatValue(data?.pageTotals.totalMetri ?? 0, 'm')} />
      <DStat label="SUB" value={formatValue(data?.pageTotals.moneySub ?? 0, 'EUR')} />
      <DStat label="MIH" value={formatValue(data?.pageTotals.moneyMih ?? 0, 'EUR')} />
      <DStat label={tc('hours')} value={formatValue(data?.pageTotals.manHours ?? 0, 'h')} />
      <DStat
        label={t('dataAsOf')}
        value={fmtDate(freshness?.dataThrough ?? null)}
        sub={t('refreshed', { ago: timeAgo(freshness?.refreshedAt ?? null) })}
      />
    </div>
  )

  // Excel export: one server query now, capped at MAX_EXCEL_ROWS (the API refuses above it).
  // The grid already knows the filtered count, so an over-limit export is explained here before
  // anyone waits on a request.
  const exportRows = data?.total ?? 0
  const tooBig = exportRows > MAX_EXCEL_ROWS
  const exportUrl = `/api/aufmass/export.xlsx?${params}`
  const exporting = excel.state.status === 'working'
  const exportLink = (
    <div className="space-y-1.5">
      <button
        type="button"
        onClick={() => { if (!tooBig) void excel.download(exportUrl, 'aufmass.xlsx') }}
        disabled={exporting || tooBig || !data}
        aria-describedby="excel-export-note"
        className="block w-full rounded-lg bg-brand-600 px-3 py-2 text-center text-xs font-semibold text-white hover:bg-brand-500 disabled:cursor-not-allowed disabled:opacity-50 max-lg:rounded-xl max-lg:py-3 max-lg:text-sm"
      >
        {exporting ? t('preparingExcel') : data && !tooBig ? t('exportExcelRows', { count: exportRows }) : t('exportExcel')}
      </button>
      <p id="excel-export-note" className="text-[11px] leading-snug text-neutral-400 max-lg:text-xs" role={excel.state.status === 'error' ? 'alert' : undefined}>
        {excel.state.status === 'error'
          ? <span style={{ color: 'var(--viz-serious)' }}>{excel.state.message}</span>
          : tooBig
            ? t('exportTooBig', { max: MAX_EXCEL_ROWS, now: exportRows })
            : null}
      </p>
    </div>
  )

  const controls = (
    <div className="space-y-3 max-lg:space-y-4">
      <Labeled label={tc('search')}>
        <div className="relative">
          <svg className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-neutral-500"
               viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
            <circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" />
          </svg>
          <input
            type="search"
            enterKeyHint="search"
            value={search}
            onChange={(e) => reset(setSearch)(e.target.value)}
            placeholder={t('searchPlaceholder')}
            aria-label={tc('search')}
            className={`${DARK_INPUT} pl-8`}
          />
        </div>
      </Labeled>
      <Labeled label={t('project')}>
        <SearchSelect
          value={projectId}
          onChange={reset(setProjectId)}
          options={(filters?.projects ?? []).map((o) => ({ ...o, name: projectName(o.name) }))}
          allLabel={t('allProjects')}
          placeholder={t('findProject')}
        />
      </Labeled>
      <Labeled label={t('firma')}>
        <SearchSelect
          value={companyId}
          onChange={reset(setCompanyId)}
          options={filters?.companies ?? []}
          allLabel={t('allFirms')}
          placeholder={t('findFirm')}
        />
      </Labeled>
      <div className="grid gap-3 max-lg:grid-cols-2">
        <Labeled label={t('record')}>
          <Select tone="dark" value={presence} onChange={reset(setPresence)} aria-label={t('record')}
            empty={{ value: '', label: t('allRecords') }}
            options={[
              { value: 'instance', label: t('reportFiled') },
              { value: 'timelog', label: t('clockedInOnly') },
              { value: 'generated', label: t('inferred') },
            ]} />
        </Labeled>
        <Labeled label={t('cabling')}>
          <Select tone="dark" value={cable} onChange={reset(setCable)} aria-label={t('cabling')}
            empty={{ value: '', label: t('allWork') }}
            options={[
              { value: 'any', label: t('anyCabling') },
              ...(filters?.cables ?? []).map((c) => ({ value: c.value, label: c.label })),
            ]} />
        </Labeled>
      </div>
      <Labeled label={t('sortLabel')}>
        <Select tone="dark" value={`${sort}:${dir}`} aria-label={t('sort')}
          onChange={(v) => {
            const [s, d] = v.split(':')
            setSort(s!); setDir(d as 'asc' | 'desc'); setPage(1)
          }}
          options={[
            { value: 'work_date:desc', label: t('newestFirst') },
            { value: 'work_date:asc', label: t('oldestFirst') },
            { value: 'metri:desc', label: t('mostMetres') },
            { value: 'money_sub:desc', label: t('highestSub') },
            { value: 'crew:asc', label: t('crewAz') },
            { value: 'city:asc', label: t('mjestoAz') },
          ]} />
      </Labeled>
      <Check checked={onlyWithWork} onChange={(v) => reset(setOnlyWithWork)(v)}>{t('withWorkOnly')}</Check>
      <Check checked={showPositions} onChange={setShowPositions}>{t('positionColumns')}</Check>

      {/* How the columns present — the user's call, not baked in. */}
      <div className="space-y-2 border-t border-white/10 pt-2 max-lg:space-y-3 max-lg:pt-4">
        <div className="flex items-center justify-between gap-2">
          <span className="text-[11px] font-medium uppercase tracking-wide text-neutral-400">{t('headers')}</span>
          <div className="flex overflow-hidden rounded-lg ring-1 ring-neutral-700">
            {(['full', 'short'] as const).map((m) => (
              <button
                key={m}
                onClick={() => setHeaderMode(m)}
                aria-pressed={headerMode === m}
                title={m === 'full' ? t('headersFullTitle') : t('headersShortTitle')}
                className={`px-2.5 py-1 text-xs max-lg:h-11 max-lg:px-5 max-lg:text-sm ${
                  headerMode === m ? 'bg-brand-600 text-white' : 'bg-neutral-800 text-neutral-300 hover:bg-neutral-700'
                }`}
              >
                {m === 'full' ? t('headersFull') : t('headersShort')}
              </button>
            ))}
          </div>
        </div>
        <Check checked={hideEmpty} onChange={setHideEmpty}>{t('hideEmptyColumns')}</Check>
        <div className="flex items-center justify-between gap-2">
          <span className="text-[11px] font-medium uppercase tracking-wide text-neutral-400">{t('zoom')}</span>
          <div className="flex items-center gap-1">
            <button onClick={() => stepZoom(-0.1)} disabled={zoom <= 0.5} aria-label={t('zoomOut')}
                    className="h-6 w-6 rounded-md bg-neutral-800 text-neutral-200 hover:bg-neutral-700 disabled:opacity-40 max-lg:h-11 max-lg:w-11 max-lg:rounded-lg max-lg:text-lg">−</button>
            <button onClick={() => setZoom(1)} className="tabular w-11 text-center text-xs text-neutral-300 hover:text-white max-lg:h-11 max-lg:w-14 max-lg:text-sm" title={t('zoomReset')}>
              {Math.round(zoom * 100)}%
            </button>
            <button onClick={() => stepZoom(0.1)} disabled={zoom >= 2} aria-label={t('zoomIn')}
                    className="h-6 w-6 rounded-md bg-neutral-800 text-neutral-200 hover:bg-neutral-700 disabled:opacity-40 max-lg:h-11 max-lg:w-11 max-lg:rounded-lg max-lg:text-lg">+</button>
          </div>
        </div>
      </div>
    </div>
  )

  const pager = (
    <div className="flex items-center justify-between gap-2 text-xs text-neutral-400 max-lg:text-sm">
      <label className="flex items-center gap-2">
        <span className="lg:sr-only">{tc('rows')}</span>
        <Select tone="dark" className="w-20" value={String(pageSize)} onChange={(v) => reset(setPageSize)(Number(v))}
          aria-label={tc('rowsPerPage')} options={PAGE_SIZES.map((n) => ({ value: String(n), label: String(n) }))} />
      </label>
      <div className="flex items-center gap-2">
        <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} aria-label={tc('previousPage')}
                className="rounded px-2 py-1 hover:bg-neutral-800 disabled:opacity-30 max-lg:grid max-lg:size-11 max-lg:place-items-center max-lg:rounded-lg max-lg:bg-neutral-800 max-lg:p-0 max-lg:text-lg">‹</button>
        <span className="tabular text-neutral-200">{page} / {totalPages}</span>
        <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages} aria-label={tc('nextPage')}
                className="rounded px-2 py-1 hover:bg-neutral-800 disabled:opacity-30 max-lg:grid max-lg:size-11 max-lg:place-items-center max-lg:rounded-lg max-lg:bg-neutral-800 max-lg:p-0 max-lg:text-lg">›</button>
      </div>
    </div>
  )

  if (error) {
    return (
      <p className="p-6 text-sm" style={{ color: 'var(--viz-critical)' }}>
        {t('couldNotLoadMeasurements')}
      </p>
    )
  }

  return (
    <div data-theme="light" className="fixed inset-0 overscroll-none bg-white text-neutral-900">
      <EditableGrid
        data={data}
        queryKey={queryKey}
        canEdit={canEdit}
        editorName={me?.name ?? t('you')}
        showPositions={showPositions}
        top={GRID_TOP}
        compact={isPhone}
        rightInset={showRail && !isPhone ? RIGHT_INSET : 0}
        rowOffset={(page - 1) * pageSize}
        sort={sort}
        dir={dir}
        onSort={onSort}
        // A phone on its side has room for a handful of rows; the stacked header would take
        // most of them, so it switches to one-line names there whatever the saved preference.
        headerMode={isShort ? 'short' : headerMode}
        hideEmpty={hideEmpty}
        zoom={zoom}
      />

      {/* Desktop: when hidden, a slim tab brings the rail back and the grid takes the freed width.
          Phone: a Filters button bottom-right, in thumb reach, opens the full-screen sheet. */}
      {!showRail && !isPhone && (
        <button
          onClick={openRail}
          title={t('showFilters')}
          className="pointer-events-auto fixed right-3 top-16 z-30 flex items-center gap-1.5 rounded-2xl bg-neutral-900/85 px-3 py-2.5 text-xs font-semibold text-neutral-100 shadow-xl ring-1 ring-white/10 backdrop-blur-xl hover:bg-neutral-900"
        >
          <svg viewBox="0 0 16 16" className="h-4 w-4 text-neutral-400" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
            <path d="M10 3.5 5.5 8l4.5 4.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          {tc('filters')}
        </button>
      )}
      {isPhone && (
        <FilterButton
          tone="dark"
          count={activeFilters}
          onClick={openRail}
          className="fixed bottom-[max(0.75rem,env(safe-area-inset-bottom))] right-3 z-30"
        />
      )}

      {isPhone ? (
        <FilterSheet
          open={showRail}
          onClose={closeRail}
          tone="dark"
          title={tc('filters')}
          subtitle={t('sheetSubtitle', { count: data?.total ?? 0, date: fmtDate(freshness?.dataThrough ?? null) })}
          onReset={resetFilters}
          resetDisabled={activeFilters === 0}
          doneLabel={t('showCrewDays', { count: data?.total ?? 0 })}
        >
          <div className="space-y-4 p-4">
            {stats}
            {exportLink}
            <div className="border-t border-white/10 pt-4">{controls}</div>
            <div className="border-t border-white/10 pt-4">{pager}</div>
          </div>
        </FilterSheet>
      ) : showRail && (
        // Translucent, so the columns it floats over stay faintly readable behind it.
        <aside className="pointer-events-auto fixed bottom-3 right-3 top-16 z-30 flex w-72 flex-col gap-3 overflow-y-auto frost-solid-dark rounded-2xl bg-neutral-900/85 p-4 text-neutral-100 shadow-xl ring-1 ring-white/10 backdrop-blur-xl backdrop-saturate-150">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-neutral-400">{tc('filters')}</span>
            <button
              onClick={closeRail}
              aria-label={t('hideFilters')}
              title={t('hideFilters')}
              className="flex h-7 w-7 items-center justify-center rounded-full text-neutral-400 hover:bg-white/10 hover:text-neutral-100"
            >
              <svg viewBox="0 0 16 16" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                <path d="M6 3.5 10.5 8 6 12.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
          </div>
          {stats}
          {exportLink}
          <div className="border-t border-white/10 pt-3">{controls}</div>
          {/* Pagination pinned to the bottom of the rail. */}
          <div className="mt-auto border-t border-white/10 pt-3">{pager}</div>
        </aside>
      )}

      {excelLoad.show && (
        <div className="pointer-events-none fixed inset-x-3 bottom-[max(4rem,calc(env(safe-area-inset-bottom)+3.5rem))] z-[65] flex justify-center">
          <LogoLoaderPill progress={excelLoad.progress} label={t('preparingExcelLabel')} />
        </div>
      )}

      {/* Status line, bottom-left, out of the way of the grid's own scrollbars. */}
      <p className="pointer-events-none fixed bottom-[max(0.75rem,env(safe-area-inset-bottom))] left-4 z-20 rounded-full bg-white/85 px-3 py-1 text-[11px] text-neutral-500 shadow-sm ring-1 ring-black/5 backdrop-blur-md max-lg:bottom-[calc(max(0.75rem,env(safe-area-inset-bottom))+0.6rem)] max-lg:left-3 max-lg:max-w-[calc(100vw-11rem)] max-lg:truncate">
        {isPhone
          ? (canEdit ? t('tapToEdit') : tc('readOnly'))
          : (canEdit
            ? t('desktopEditHint')
            : t('readOnlyNeedsPermission'))}
        {edited > 0 && ` · ${t('correctedCells', { count: edited })}`}
      </p>
    </div>
  )
}

function Labeled({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[10px] font-medium uppercase tracking-wide text-neutral-400 max-lg:mb-1.5 max-lg:text-[11px]">{label}</span>
      {children}
    </label>
  )
}

function Check({ checked, onChange, children }: { checked: boolean; onChange: (v: boolean) => void; children: ReactNode }) {
  return (
    <label className="flex items-center gap-2 text-xs text-neutral-300 max-lg:min-h-10 max-lg:gap-3 max-lg:text-sm">
      <input
        type="checkbox" checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="rounded border-neutral-600 bg-neutral-800 text-brand-500 max-lg:size-5"
      />
      {children}
    </label>
  )
}

function DStat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="min-w-0">
      <div className="tabular truncate text-sm font-semibold leading-tight text-white max-lg:text-base">{value}</div>
      <div className="text-[10px] uppercase tracking-wide text-neutral-400">{label}</div>
      {sub && <div className="truncate text-[9px] text-neutral-500">{sub}</div>}
    </div>
  )
}
